/**
 * The Sale domain — an ADAPTER over the existing, kept-not-rewritten
 * `packages/core/payments/calculate.ts` (sale_util, the four commit
 * destinations, the per-type validator matrix). See
 * docs/flows/PAYMENT_ENGINE.md §8 for why that file is ported rather than
 * redesigned: it is v1's behaviour written down, not architecture.
 *
 * This file's only job is the boundary: the engine's generic `Leg` <->
 * calculate.ts's sale-shaped `PaymentLeg`. Everything sale-specific — txnType,
 * NC, returns, tip, rounding — lives in `SaleRecord`, never in the engine.
 */
/**
 * Relative, not `@rista/core/payments/…`: the bare specifier needs a real
 * `pnpm install` to materialise the workspace symlink, and this package's
 * verification is node checks with NO install step. This file has no platform
 * variant to resolve.
 */
import {
  buildCommit as buildSaleCommit,
  buildRequestSale,
  availableForLeg,
  capNonCashAmount,
  roundLegAmount,
  selectActiveLegValid,
  selectCanClose,
  selectCanCommit,
  selectCanRemoveLeg,
  selectCloseBlockedReason,
  selectOverRefund,
  selectTotals,
} from "../../money/calculate";
import type { CommitIntent, PaymentTotals } from "../../money/calculate";
import { LEG_STATUS, TXN_TYPE } from "../../money/types";
import type { PaymentLeg, TxnType } from "../../money/types";
import { LEG, LEG_STATE_TO_WIRE } from "../../engine/machine";
import type { Leg, ModeRef, PaymentDomain } from "../../engine/types";

/**
 * The record's definition of a mode. A NAME resolves to the first entry that
 * carries it, which is wrong whenever a merchant has configured more than one
 * terminal of the same provider — so callers pass the definition itself
 * wherever they have it, as v1 does.
 */
function defFor(record: SaleRecord, mode: ModeRef): any {
  if (mode && typeof mode !== "string") return mode;
  return (record.modes ?? []).find((m: any) => m.mode === mode) ?? {};
}

const nameOf = (mode: ModeRef): string =>
  (typeof mode === "string" ? mode : mode?.mode) ?? "";

export interface SaleRecord {
  sale: any;
  /** the host's `getParamsForSaleCalculate(sale)` — the library needs catalog context */
  params: any;
  returnSale?: boolean;
  txnType?: TxnType;
  closeReason?: { reason?: string; remarks?: string } | null;
  tip?: number;
  allowOpenBalance?: boolean;
  /**
   * DISABLE_PAYMENT_SAVE not restricted: SAVE may go through whatever is still
   * owed. It never opens CONFIRM — closing with a balance is `allowOpenBalance`.
   */
  allowSaveOverride?: boolean;
  /**
   * The merchant's permitted payment defs. Needed because `isCash` is a
   * property of the MODE, not of the leg the operator has not created yet —
   * `priceLeg` reads it to decide whether over-tendering is allowed.
   */
  modes?: any[];
}

function wireToLegState(leg: any): Leg["legState"] {
  switch (leg?.paymentStatus) {
    case LEG_STATUS.REQUESTED:
      return LEG.REQUESTED;
    case LEG_STATUS.PENDING:
      return LEG.PENDING;
    case LEG_STATUS.POSTED:
      return LEG.POSTED;
    case LEG_STATUS.FAILED:
      return LEG.FAILED;
    default:
      return leg?.posted ? LEG.POSTED : LEG.DRAFT;
  }
}

/**
 * Sale leg -> engine leg. Sale-specific fields travel opaquely in `leg.data`,
 * except `paymentStatus`/`posted`, which are STRIPPED: they are the wire's
 * spelling of machine state, and `normalizePayments` reads both to decide what
 * counts as collected. `legState` is the single source of truth and `toSaleLeg`
 * re-derives them on the way out.
 */
function toEngineLeg(leg: PaymentLeg): Leg {
  const {
    paymentId,
    mode,
    amount,
    requestAmount,
    legState,
    reason,
    errorMessage,
    draft,
    paymentStatus: wireStatus,
    posted: _wirePosted,
    ...rest
  } = leg;
  return {
    paymentId,
    mode,
    amount,
    requestAmount,
    legState: legState ?? wireToLegState(leg),
    reason,
    errorMessage,
    // only present when it is TRUE — see the note on Leg.draft
    ...(draft ? { draft: true } : {}),
    /**
     * `wireStatus` records that this leg's `paymentStatus` came FROM the server,
     * which decides whether the payload may carry it back (see
     * `stripMintedStatus`). Deliberately NOT spelled `paymentStatus`: that is
     * the key `normalizePayments:75` reads, and putting the wire's own spelling
     * back in the bag is defect #7 — `legState` stays the single source of
     * machine state. `toSaleLeg` strips it, so it never reaches the wire.
     */
    data: { ...rest, ...(wireStatus ? { wireStatus } : {}) },
  };
}

/**
 * Engine leg -> sale leg, re-deriving the wire fields from `legState`.
 *
 * `code` is dropped: it is the engine's own carrier for a provider rejection
 * and `PaymentLeg` has no such field, so passing it through would put a key on
 * the wire that v1 never sends — a DD3 parity break while v1 and v2 terminals
 * sync over LAN.
 */
export function toSaleLeg(leg: Leg): PaymentLeg {
  /**
   * `provider` is ENGINE-ONLY and does not go on the wire.
   *
   * It exists because the server renames `mode` — a pinelabs leg comes back as
   * `"pinelabs - Card"` — so the registry key has to be kept separately (see `Leg.provider`).
   * That is a reason to hold it in the SESSION, not a reason to send it: v1 posts
   * `{ paymentId, type, mode, amount, pid, img, originalAmount, requestAmount }` and no
   * `provider`, and byte parity with v1 is a rollout precondition while v1 and v2 terminals
   * sync sales to each other over LAN. Same treatment, and the same reasoning, as `legState`
   * below.
   *
   * ⚠️ The in-memory leg keeps it: this builds a new object, and `adoptLegs` SPREADS the
   * server's leg over ours, so a response without the key cannot delete it.
   *
   * ⚠️ What this does give up is a sale RE-OPENED from scratch, where there is no in-memory
   * leg to keep it on — `selectPane`'s `leg.provider ?? leg.mode` then falls back to a `mode`
   * the server may have renamed. v1 has the same gap and answers it by routing the in-flight
   * screen by FLOW rather than re-deriving from the mode; if that ever bites here, the fix is
   * to re-derive on adopt, not to put an engine field back on the wire.
   */
  const { data: bag, code: _engineOnly, legState, provider: _engineKey, ...flat } = leg;
  // engine-only: provenance for `stripMintedStatus`, never a wire field
  const { wireStatus: _provenance, ...data } = (bag ?? {}) as Record<string, any>;
  const wireStatus = LEG_STATE_TO_WIRE[legState];
  /**
   * The provider's blob goes out EXACTLY as the provider wrote it.
   *
   * A `paymentId` was stamped in here after two terminal reports showed v1
   * sending `data: { deviceId, mode, paymentId }`. A first-initiate capture
   * from v1 then showed `data: { deviceId, mode }` — the id is added by the
   * SERVER, and both earlier captures were saves of an already-requested leg,
   * which also explains their `paymentStatus`, `paymentBy`, `postedDate` and
   * `transactionId`. The stamp is reverted; `adoptLegs` carries the server's
   * blob back whole (§18), so it still reaches later saves.
   */
  return {
    ...data,
    ...flat,
    /**
     * `legState` is NOT on the wire. It is the engine's machine state and v1
     * has no such field — sending it put an engine-internal on every leg of
     * every `sale/payment/save`, which is exactly the DD3 break the
     * present-or-absent rule below exists to prevent. Re-opening derives the
     * state from `paymentStatus` (`wireToLegState`), which is what v1's own
     * legs carry, so nothing needs it back.
     */
    /**
     * PRESENT-OR-ABSENT, never written for a leg that has not been anywhere.
     *
     * v1 mints a leg with neither key: `paymentStatus` first appears when the
     * payment is requested and `posted` when a provider answers. Writing
     * `posted: false` onto every cash leg puts a field on the wire that v1
     * never sends — the same class of break as the `draft: false` the engine is
     * already careful about, and byte parity with v1 is a rollout precondition
     * while v1 and v2 terminals sync sales to each other over LAN.
     *
     * REPORTED: `posted: false` rode on a REQUESTED leg. v1's requested leg
     * carries `paymentStatus: "Requested"` and NO `posted` key at all — the
     * field first appears as `posted = true` when a provider completes
     * (`handleGetExtCb:1067`), and v1's failure branch sets only
     * `errorMessage` + `paymentStatus`, never `posted: false`. So it is written
     * for a POSTED leg and for nothing else.
     */
    ...(wireStatus ? { paymentStatus: wireStatus } : {}),
    ...(legState === LEG.POSTED ? { posted: true } : {}),
  } as PaymentLeg;
}


/**
 * Where a commit goes. FOUR destinations, and the ORDER matters — `preventEdit`
 * wins over a return (v1 `handleCheckoutAction:432`).
 *
 * Decided here rather than in the app's transport so the routing is a pure
 * function with fixtures, leaving the transport a switch on a name.
 */
export type SaleDestination = "save" | "close" | "updatePaymentClose" | "saleReturnClose";

export interface SaleCommit {
  destination: SaleDestination;
  /** what to POST — the whole sale, EXCEPT updatePaymentClose (see below) */
  body: any;
  /** `updatePaymentClose` sends the mid alongside the body, not inside it */
  mid?: string;
  /**
   * Is this a CLOSE? The transport stamps close-only host data off this —
   * the ticket flag, deleting `requestOrderApproval`, and attaching pending
   * discount approvals. All three are close-only in v1: they live inside
   * `handleCheckoutAction` and neither save path touches them.
   */
  closing: boolean;
  intent: string;
  totals: PaymentTotals;
}

export function routeCommit(record: SaleRecord, intent: string): {
  destination: SaleDestination;
  closing: boolean;
} {
  // an already-CLOSED sale only updates its payment method — v1 routes it to
  // handleSaveOrder BEFORE any close branch is reached (`:439`), so it never
  // closes twice
  const closing = intent === "close" && record.sale?.saleStatus !== "Closed";
  if (!closing) return { destination: "save", closing: false };
  // preventEdit wins over a return — the order is v1's and is load-bearing
  if (record.sale?.preventEdit) return { destination: "updatePaymentClose", closing: true };
  if (record.returnSale) return { destination: "saleReturnClose", closing: true };
  return { destination: "close", closing: true };
}

/**
 * A refund cannot exceed what the original sale still owes.
 *
 * NOT on `PaymentDomain` — the engine has no idea a record can be refunded.
 * An extra export of the sale domain module, like the txn-type helpers, called
 * by the sale shell only.
 *
 * v1 checks this at the top of `handleCheckoutAction` and pops
 * RETURN_PAYMENT_ERROR after the press; here it disables the CTA and says why,
 * and `buildCommit` still throws it as a backstop (§11).
 */
/**
 * May the operator close the checkout? `handleCheckoutClose:549` guards it with
 * TWO conditions: a payment in flight, and `!CLOSE_SALE_BALANCE &&
 * !checkValidAmount(tendered)` — walking away from an uncovered bill is a
 * PERMISSION, and v1 applies it to the cross, the backdrop and Escape alike
 * (§11d). Returns the message, or null when closing is allowed.
 */
export function closeBlocked(
  legs: Leg[],
  totals: PaymentTotals,
  record: SaleRecord,
  settling: boolean
): string | null {
  const calcLegs = legs.map(toSaleLeg);
  const opts = { allowOpenBalance: record.allowOpenBalance, settling };
  if (selectCanClose(calcLegs, record, totals, opts)) return null;
  return selectCloseBlockedReason(calcLegs, record, totals, opts);
}

export function overRefund(legs: Leg[], record: SaleRecord): boolean {
  return selectOverRefund(legs.map(toSaleLeg), record);
}

/**
 * v1's `validatePayments` on the SELECTED leg — a mandatory reference filled in,
 * and a non-cash amount within the balance (`paymenthelper:689`).
 *
 * NOT on `PaymentDomain`: the engine needs it for exactly one thing, gating ADD
 * PAYMENT the way v1 does (`validAmountSelected && isCtaActive()`,
 * `defaultFlow:158`), and a wallet has no such rule. Handed in by the shell.
 */
/**
 * May the OPERATOR delete this leg? v1's `isSimpleDeletable`
 * (`SplitComponentDesktop:56`) — `!paymentStatus && mode !== 'Coupon' &&
 * type !== External`. Only the first is implied by the engine's `legState ===
 * DRAFT`; the other two are provenance, which is sale-shaped.
 */
export function canRemoveLeg(legs: Leg[], _record: SaleRecord, paymentId: string): boolean {
  return selectCanRemoveLeg(legs.map(toSaleLeg), paymentId);
}

/**
 * Does a leg picked in this mode start OUTSIDE the totals? v1
 * `UpdatePaymentLeg:611` registers a leg immediately unless it is a SPLIT that
 * is not an external (gyftr excepted), in which case it is held in
 * `selectedPayment` until ADD PAYMENT. The question is the leg's TYPE, off the
 * payment def — nothing about the provider.
 */
export function startsAsDraft(record: SaleRecord, mode: string): boolean {
  if (record.txnType !== TXN_TYPE.SPLIT) return false;
  const def = defFor(record, mode);
  return !(def?.type === "External" && nameOf(mode) !== "gyftr");
}

/**
 * The balance the operator is tendering AGAINST — v1's
 * `localPaymentInfo.balanceAmount`, which is NOT decremented for the leg being
 * edited (§11b).
 *
 * `availableForLeg` is that rule, and the only copy of it — the pricing cap asks
 * the same question and must get the same answer.
 *
 * Takes the caller's totals: `selectTotals` does not read `activeLegId`, so
 * recomputing would return identical numbers for a `structuredClone` of the
 * sale plus a full `calculateForSale`, on every render.
 */
export function balanceWithoutLeg(
  legs: Leg[],
  totals: PaymentTotals,
  paymentId?: string
): number {
  const leg = legs.find((l) => l.paymentId === paymentId);
  // the cast is the two types' shared subset — `availableForLeg` reads only
  // `draft` and `amount`, both of which an engine Leg carries
  return availableForLeg(totals, leg as any);
}

/**
 * May the operator SELECT this leg to edit it? v1's `isValidSelection`
 * (`SplitComponentDesktop:262`) is `type === 'Custom' || type === 'System'` —
 * `payments.d.ts`'s set minus `External`, so: locally tendered. An external
 * leg's money is the provider's answer, not the operator's to retype.
 *
 * A DIFFERENT question from `canRemoveLeg`, on a different line in v1 with
 * different rules; kept separate for the day they diverge.
 */
export function canSelectLeg(legs: Leg[], _record: SaleRecord, paymentId: string): boolean {
  const leg = legs.find((l) => l.paymentId === paymentId);
  const type = leg?.data?.type;
  return type === "Custom" || type === "System";
}

export function legValid(
  legs: Leg[],
  totals: PaymentTotals,
  _record: SaleRecord,
  paymentId: string
): boolean {
  return selectActiveLegValid(legs.map(toSaleLeg), paymentId, totals);
}

/**
 * v1 does NOT mint `paymentStatus: "Requested"`.
 *
 * The capture of a first `sale/payment/save` for a fresh pinelabs payment has
 * nine keys and no status at all — the SERVER sets it, and v1 echoes it back on
 * any later save of that leg. Every client-side write in v1 is a settlement or
 * a failure:
 *
 *   PaymentMethods:89                    REQUESTED  — Prepaid, and only Prepaid
 *   CheckoutContainer:980, :1066         Posted     — on a completion response
 *   :725/:776/:945/:1031/:1106,          FAILED     — on failure
 *   AuthCodeModal:66, EDCFlow:113
 *   :805                                 REFUNDED   — on a refund
 *
 * So the field is stripped from the PAYLOAD when the engine is the one that
 * derived it — never from `toSaleLeg`, because `normalizePayments:75` reads
 * `paymentStatus` to decide what counts as awaiting, and dropping it there
 * would zero the operator's Awaiting figure.
 *
 * A leg SEEDED from the server keeps it (`data.wireStatus`), which is v1's
 * behaviour on a re-opened sale — §7's row. That provenance is the whole reason
 * this is not just "delete it when REQUESTED".
 */
const MINTED_BY_SERVER = ["Requested", "Pending"];

function stripMintedStatus(payments: any[] | undefined, legs: Leg[]): void {
  if (!payments) return;
  payments.forEach((payment) => {
    if (!MINTED_BY_SERVER.includes(payment?.paymentStatus)) return;
    // Prepaid is v1's one exception: it mints REQUESTED at pick time
    if (payment.mode === "Prepaid") return;
    const leg = legs.find((candidate) => candidate.paymentId === payment.paymentId);
    if (leg?.data?.wireStatus) return;
    delete payment.paymentStatus;
  });
}

export const saleDomain: PaymentDomain<SaleRecord, PaymentTotals, SaleCommit> = {
  seed(record) {
    // mirrors `prepareLocalPaymentInfo:226` — the only legs a session starts
    // with are the sale's own; nothing else is ever seeded (§12)
    return ((record.sale?.paymentInfo?.payments ?? []) as PaymentLeg[]).map(toEngineLeg);
  },

  totals(legs, record) {
    return selectTotals(legs.map(toSaleLeg), record, record.params);
  },

  /**
   * The auto-persist's close-or-save question, and v1 asks it exactly:
   * `if (paymentInfo.balanceAmount != 0) save = true` (`:897`). NOT
   * `selectCoversBill`, whose sub-₹1 tolerance belongs to the operator's
   * Confirm gate (§14a).
   */
  cleared(totals) {
    return totals.balance === 0;
  },

  canCommit(legs, totals, record, ctx) {
    if (ctx.intent === "save" && record.allowSaveOverride) return { ok: true };
    const calcLegs = legs.map(toSaleLeg);
    const opts = { allowOpenBalance: record.allowOpenBalance, settling: false, activeLegId: ctx.activeLegId };
    const ok = selectCanCommit(calcLegs, record, totals, opts);
    return {
      ok,
      reason: ok ? undefined : selectCloseBlockedReason(calcLegs, record, totals, opts) ?? undefined,
    };
  },

  buildCommit(legs, _totals, record, intent) {
    const built = buildSaleCommit(legs.map(toSaleLeg), record, record.params, intent as CommitIntent);
    // the commit is a payload too — a leg still awaiting its provider rides on
    // a save, and v1 does not put a status on it either
    stripMintedStatus(built.sale?.paymentInfo?.payments, legs);
    const { destination, closing } = routeCommit(record, intent);
    return {
      destination,
      closing,
      intent,
      totals: built.totals,
      // `updatePaymentClose` posts the paymentInfo ALONE plus the mid (`:477`).
      // v2 posted the WHOLE sale to `closeSaleFn` for this case until §10b was
      // written — a different endpoint AND a different body.
      body: destination === "updatePaymentClose" ? { ...built.sale.paymentInfo } : built.sale,
      ...(destination === "updatePaymentClose" ? { mid: built.sale.mid } : {}),
    };
  },

  /**
   * Cap, then round, both from the kept helpers. Rounding is a property of the
   * MODE (`roundOffBillAmountBasedOnPaymentModes` reads only `payment.isCash`),
   * which is why this takes the mode rather than the leg and why re-picking
   * re-prices — v1 keeps the leg in step from an effect keyed on the selected
   * mode (`TouchInput:44`).
   */
  priceLeg(legs, record, { mode, typed, paymentId }) {
    const existingLeg = paymentId ? legs.find((l) => l.paymentId === paymentId) : undefined;
    /**
     * The MODE ARGUMENT decides, always — never the leg. A re-price is very
     * often a mode SWITCH, and the leg still carries the old mode's `isCash`
     * until this returns; reading it there is what §9 is about.
     *
     * Resolving a bare NAME is safe for this one field: `isCash` is a property
     * of the mode, so a merchant's several terminals of one provider all agree
     * on it even though their `meta.pid` differs.
     */
    const def = defFor(record, mode);
    const isCash = !!def?.isCash;
    /**
     * The leg is re-moded BEFORE the totals. The library rounds the bill on
     * `payment.isCash` (`sale_util.js:1951`), so a 701.5 bill still reads 702
     * while the leg being switched still says Cash — and the cap then allows 702
     * on a bill that is 701.5 for a card. v1 re-runs `roundFor` on the selected
     * MODE for the same reason (`TouchInput:44`).
     */
    const calcLegs = legs.map((leg) => {
      const wire = toSaleLeg(leg);
      if (leg.paymentId !== paymentId) return wire;
      return { ...wire, mode: nameOf(mode) || wire.mode, type: def?.type, isCash };
    });
    const totals = selectTotals(calcLegs, record, record.params);

    /**
     * The base to price FROM: what is still owed, or — for an existing leg —
     * its `originalAmount`, the PRE-rounding figure. Re-pricing from `amount`
     * makes the rounding cumulative (§9), and `totals.balance` carries the sign
     * so a refund leg is negative from birth (§11).
     */
    const existing = existingLeg;
    const wanted =
      typed ?? (existing?.data?.originalAmount as number | undefined) ?? existing?.amount ?? totals.balance;

    // cash may over-tender (that is how change happens); non-cash may not.
    // A leg that does not exist yet is the same rule with nothing to add back.
    const capped = capNonCashAmount(calcLegs, paymentId ?? "", wanted, totals, isCash);

    /**
     * Rounding is the LIBRARY's, for this mode, and nothing is subtracted from
     * it afterwards: `roundOffBillAmountBasedOnPaymentModes` exempts a non-cash
     * mode only when the merchant set `roundOnlyCashPaymentMode`
     * (`sale_util.js:1938`), and when it does not, v1 charges the rounded figure
     * — the BILL is rounded the same way, so the leg still lands on it. v1
     * composes exactly this: `roundFor(value)` then the clamp (`TouchInput:44`).
     */
    const amount = roundLegAmount({ isCash }, capped, record.params);

    // was the request REDUCED? Non-cash only — cash may over-tender, and
    // rounding is not a refusal. v1 says so before clamping (`TouchInput:144`)
    const cappedCode =
      !isCash && typed !== undefined && Math.abs(capped) < Math.abs(typed)
        ? "UI_MSG_AMOUNT_GT_BALANCE_NONCASH"
        : undefined;

    /**
     * `originalAmount` is the base this pricing ROUNDED, and it is post-clamp.
     * v1 stamps `originalValue` beside the validated amount (`TouchInput:171`),
     * and the effect that fires on a mode change passes `selectedAmount` — the
     * leg's CURRENT amount, already clamped by the switch (`:45-48`). So a clamp
     * is destructive: 500 tendered in cash on a 300 bill becomes 300 on the way
     * to a card and stays 300 on the way back. Only the ROUNDING is reversible,
     * which is what this figure exists for (§17).
     */
    return { amount, originalAmount: capped, ...(cappedCode ? { cappedCode } : {}) };
  },

  /**
   * Mirrors `createLeg`'s field copying (`packages/core/payments/session.ts:162`).
   * Present-or-absent, never an explicit `false`/`undefined` — v1 DELETES these
   * keys when the mode does not carry them, and byte parity is a precondition.
   */
  newLegData(record, mode) {
    const def = defFor(record, mode);
    const data: Record<string, any> = {};
    if (def.type !== undefined) data.type = def.type;
    if (def.isCash !== undefined) data.isCash = def.isCash;
    if (def.referenceMandatory) data.referenceMandatory = def.referenceMandatory;
    if (def.reference) data.reference = def.reference;
    // a device provider posts `{ pid }` to fetch its terminal list — without
    // this it fetches with `undefined` and the screen renders an empty picker
    if (def.meta) {
      data.pid = def.meta.pid;
      data.img = def.meta.img;
    }
    return { data };
  },

  /**
   * v1's pre-hand-off gate, both halves and in v1's order
   * (`pineLabsComp:87-90` and its three siblings).
   *
   * `validatePayments` first — the reference and the cap, read off the leg so
   * each branch keeps v1's OWN message rather than collapsing into one — then,
   * in FULL only, `checkValidAmount`, which is `selectCoversBill` (sub-₹1
   * tolerance included) and exempts a return exactly as v1's does.
   */
  handoffBlocked(legs, totals, record, paymentId) {
    const leg = legs.find((candidate) => candidate.paymentId === paymentId);
    if (leg?.amount) {
      if (leg.data?.referenceMandatory && !leg.data?.reference) {
        return { code: "UI_VALIDATION_ERR_REQUIRED", param: "Reference" };
      }
      if (!leg.data?.isCash && leg.legState !== LEG.POSTED) {
        if (Math.abs(leg.amount) > availableForLeg(totals, toSaleLeg(leg))) {
          return { code: "UI_MSG_AMOUNT_GT_BALANCE_NONCASH" };
        }
      }
    }
    /**
     * `checkValidAmount(selectedPayment.amount)` (`paymenthelper:424`), and its
     * OPERANDS matter — v1 does not derive this from the session:
     *
     *   saleData.paymentInfo.balanceAmount - enteredAmount >= 1  ->  refused
     *
     * The left side is the RECORD's own balance, the right is the leg's amount
     * read straight off the leg. `selectCoversBill(totals)` is NOT the same
     * question: an unposted External leg collects nothing
     * (`getNewPaymentInfo`, pinned in `domains/sale/parity.test.ts`), so `totals.balance` never
     * subtracts it — and every provider that gates this is External, so that
     * form refused every hand-off at every amount.
     *
     * The sub-₹1 tolerance is v1's, mirrored rather than tightened.
     */
    const full = (record.txnType ?? TXN_TYPE.FULL) === TXN_TYPE.FULL;
    if (full && !record.returnSale) {
      const owed = Number(record.sale?.paymentInfo?.balanceAmount ?? 0);
      if (owed - Math.abs(leg?.amount ?? 0) >= 1) {
        return { code: "UI_MSG_AMOUNT_LESS_THAN_BALANCE" };
      }
    }
    return null;
  },

  buildInitiatePayload(legs, _leg, record) {
    const payload = buildRequestSale(legs.map(toSaleLeg), record, record.params);
    stripMintedStatus(payload?.paymentInfo?.payments, legs);
    return payload;
  },

  /** The four questions the engine asks but cannot answer. */
  closeBlocked,
  legValid,
  canRemoveLeg,
  canSelectLeg,
  startsAsDraft,

  /**
   * The return closes as v1's does (`CheckoutContainer:503`): its saga says "Sale return order
   * has been created", and nothing prints or resets after it. A REFUSED return is not quiet: it
   * never gets here, and the session stays open with the error (§36 G1, the user's call).
   */
  quietClose: (commit) => commit.destination === "saleReturnClose",

  adopt(response, record) {
    const sale = response as any;
    if (!sale || sale.code) return { record, legs: record.sale?.paymentInfo?.payments?.map(toEngineLeg) ?? [] };
    const legs = ((sale.paymentInfo?.payments ?? []) as PaymentLeg[]).map(toEngineLeg);
    return { record: { ...record, sale }, legs };
  },
};
